import {
	API_ASSIGN_SECRETARY_PRESCRIPTION,
	API_BULK_ANALYSES_PRESCRIPTION,
	API_DELETE_PRESCRIPTION,
	API_DELETE_SCAN_PRESCRIPTION,
	API_GET_BY_BARCODE_PRESCRIPTION,
	API_PATCH_PRESCRIPTION,
	API_POST_PRESCRIPTION,
	API_POST_SCAN_PRESCRIPTION,
	API_POST_SCAN_PRESCRIPTION_UPDATE_PRESC,
	API_UNASSIGN_SECRETARY_PRESCRIPTION,
	API_VALIDATE_PRESCRIPTION,
	API_SET_SAMPLER_PRESCRIPTION,
	API_GET_FPA_PDF,
	API_GET_REPEATABLE_PRESCRIPTION,
	API_POST_REPEATABLE_PRESCRIPTION,
	API_POST_PRESCRIPTION_SCAN_BARCODE,
	API_POST_SAFEPLACE_RESULT_PRESCRIPTION,
	API_POST_IATA_DID_PRESCRIPTION,
	API_GET_DSP_PRESCRIPTION,
	API_PUT_VALID_PRESCRIPTION_SCAN,
	API_PUT_QUALITY_PRESCRIPTION_SCAN,
	API_PUT_BAD_QUALITY_PRESCRIPTION_SCAN,
	API_GET_SCANS,
	API_PUT_SCAN_ORDO,
	API_DELETE_SCAN_ORDO,
	CLEAN_SCANS,
	API_GET_MY_SECU_CHECK,
	CLEAN_MY_SECU_CHECK,
	API_GET_RELOAD_SCANS,
	API_GET_MYLAB_ORDO,
	API_PUT_REFUSED_ANALYSIS,
	API_GET_EPN_PDF,
	API_PATCH_DISPENSES_ANALYSES,
	API_PATCH_CHECK_ANALYSIS,
	API_GET_ALL_SCANS,
	API_PATCH_PRESCRIPTION_CONTACT,
} from "actions/prescription";
import { DEFAULT_CLIENT, V2_CLIENT } from "config/clientConstants";

export const getVersion = (action) => {
	const path = pathMapping[action];
	return {
		path: path ? path[path.currentClient] : undefined,
		currentClient: path ? path.currentClient : DEFAULT_CLIENT,
	}
}

/**
 * Converts a value to a number if possible, otherwise returns the original value
 */
const toNumber = (value) => {
	if (value === null || value === undefined) return value;
	const num = typeof value === 'string' ? parseFloat(value) : Number(value);
	return isNaN(num) ? value : num;
};

/**
 * Adapts prescription data from legacy format to NestJS format
 * @param {Object} prescription - Legacy prescription object
 * @returns {Object} - NestJS formatted prescription object
 */
const adaptPrescriptionBody = (prescription) => {
	if (!prescription) return prescription;

	const adapted = { ...prescription };

	// Map legacy IDs to NestJS format and ensure they are numbers
	if (adapted.prescriber_id !== undefined) {
		adapted.prescriberId = toNumber(adapted.prescriber_id);
		delete adapted.prescriber_id;
	}
	if (adapted.prescriber?.id !== undefined) {
		adapted.prescriberId = toNumber(adapted.prescriber.id);
		delete adapted.prescriber;
	}

	if (adapted.patient !== undefined) {
		adapted.patientId = toNumber(adapted.patient);
		delete adapted.patient_id;
	}
	if (adapted.patient?.id !== undefined) {
		adapted.patientId = toNumber(adapted.patient.id);
		delete adapted.patient;
	}

	if (adapted.folder_id !== undefined) {
		adapted.folderId = toNumber(adapted.folder_id);
		delete adapted.folder_id;
	}
	if (adapted.folder !== undefined) {
		const folderValue = typeof adapted.folder === 'number' ? adapted.folder : toNumber(adapted.folder);
		if (folderValue !== undefined && !isNaN(folderValue)) {
			adapted.folderId = folderValue;
		}
		delete adapted.folder;
	}

	if (adapted.billing_company_id !== undefined) {
		adapted.billingCompanyId = toNumber(adapted.billing_company_id);
		delete adapted.billing_company_id;
	}
	if (adapted.billing_company?.id !== undefined) {
		adapted.billingCompanyId = toNumber(adapted.billing_company.id);
		delete adapted.billing_company;
	}

	if (adapted.mylab_prescription_id !== undefined) {
		adapted.mylabPrescriptionId = toNumber(adapted.mylab_prescription_id);
		delete adapted.mylab_prescription_id;
	}

	if (adapted.agence_prescription_id !== undefined) {
		adapted.agencePrescriptionId = adapted.agence_prescription_id;
		delete adapted.agence_prescription_id;
	}

	// Convert snake_case fields to camelCase
	const fieldMap = {
		to_be_faxed: 'toBeFaxed',
		to_be_phoned: 'toBePhoned',
		to_be_faxed_number: 'toBeFaxedNumber',
		to_be_phoned_number: 'toBePhonedNumber',
		phone_before: 'phoneBefore',
		fax_before: 'faxBefore',
		accident_number: 'accidentNumber',
		accident_date: 'accidentDate',
		paper_copy_to_patient: 'paperCopyToPatient',
		fax_to_patient: 'faxToPatient',
		fax_to_patient_number: 'faxToPatientNumber',
		phone_to_patient: 'phoneToPatient',
		phone_to_patient_number: 'phoneToPatientNumber',
		show_mylab: 'showMylab',
		notification_mylab: 'notificationMylab',
		to_be_completed: 'toBeCompleted',
		to_be_sampled: 'toBeSampled',
		home_prescribed: 'homePrescribed',
		prescriber_to_create_by_secretary: 'prescriberToCreateBySecretary',
		doctor_copy_to_create_by_secretary: 'doctorCopyToCreateBySecretary',
		no_prescriber: 'noPrescriber',
		fax_to_billing_company: 'faxToBillingCompany',
		phone_to_billing_company: 'phoneToBillingCompany',
		fax_to_billing_company_number: 'faxToBillingCompanyNumber',
		phone_to_billing_company_number: 'phoneToBillingCompanyNumber',
		safeplace_code: 'safeplaceCode',
		safeplace_result: 'safeplaceResult',
		iata_travel_pass_id: 'iataTravelPassId',
		validation_count: 'validationCount',
		validity_date: 'validityDate',
		issue_date: 'issueDate',
	};

	Object.keys(fieldMap).forEach((legacyKey) => {
		if (adapted[legacyKey] !== undefined) {
			adapted[fieldMap[legacyKey]] = adapted[legacyKey];
			delete adapted[legacyKey];
		}
	});

	// Ensure numeric fields are numbers (including if they're already in camelCase)
	const numericFields = ['patientId', 'prescriberId', 'folderId', 'billingCompanyId', 'mylabPrescriptionId', 'validationCount'];
	numericFields.forEach((field) => {
		if (adapted[field] !== undefined && adapted[field] !== null) {
			adapted[field] = toNumber(adapted[field]);
		}
	});

	// Ensure boolean fields are booleans
	const booleanFields = [
		'urgent', 'toBeFaxed', 'toBePhoned', 'accident', 'paperCopyToPatient',
		'faxToPatient', 'phoneToPatient', 'showMylab', 'notificationMylab',
		'repeatable', 'toBeCompleted', 'toBeSampled', 'homePrescribed',
		'prescriberToCreateBySecretary', 'doctorCopyToCreateBySecretary',
		'noPrescriber', 'faxToBillingCompany', 'phoneToBillingCompany'
	];
	booleanFields.forEach((field) => {
		if (adapted[field] !== undefined) {
			adapted[field] = Boolean(adapted[field]);
		}
	});

	return adapted;
}

const pathMapping = {
	[API_POST_PRESCRIPTION]: {
		default: "/prescriptions",
		v2: "/prescriptions",
		currentClient: DEFAULT_CLIENT,
	},
	[API_PATCH_PRESCRIPTION]: {
		default: (prescription) => `/prescriptions/${prescription.id}`,
		v2: (prescription) => `/prescriptions/${prescription.id}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_DELETE_PRESCRIPTION]: {
		default: (prescription) => `/prescriptions/${prescription.id}`,
		v2: (prescription) => `/prescriptions/${prescription.id}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_POST_SCAN_PRESCRIPTION]: {
		default: (prescriptionId) => `/prescriptions/${prescriptionId}/scan`,
		v2: (prescriptionId) => `/prescriptions/${prescriptionId}/scan`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_DELETE_SCAN_PRESCRIPTION]: {
		default: (prescriptionScan) => `/prescriptions/scan/${prescriptionScan.id}`,
		v2: (prescriptionScan) => `/prescriptions/scan/${prescriptionScan.id}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_POST_SCAN_PRESCRIPTION_UPDATE_PRESC]: {
		default: (prescriptionScanId) => `/prescriptions/scan/${prescriptionScanId}/prescri`,
		v2: (prescriptionScanId) => `/prescriptions/scan/${prescriptionScanId}/prescri`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_PUT_VALID_PRESCRIPTION_SCAN]: {
		default: (prescriptionScan) => `/prescriptions/scan/valid/${prescriptionScan.id}`,
		v2: (prescriptionScan) => `/prescriptions/scan/valid/${prescriptionScan.id}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_PUT_QUALITY_PRESCRIPTION_SCAN]: {
		default: (prescriptionScan) => `/prescriptions/scan/quality/${prescriptionScan.id}`,
		v2: (prescriptionScan) => `/prescriptions/scan/quality/${prescriptionScan.id}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_PUT_BAD_QUALITY_PRESCRIPTION_SCAN]: {
		default: (prescriptionScan) => `/prescriptions/scan/badquality/${prescriptionScan.id}`,
		v2: (prescriptionScan) => `/prescriptions/scan/badquality/${prescriptionScan.id}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_BULK_ANALYSES_PRESCRIPTION]: {
		default: (prescription) => `/prescriptions/${prescription.id}/analyses`,
		v2: (prescription) => `/prescriptions/${prescription.id}/analyses`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_GET_BY_BARCODE_PRESCRIPTION]: {
		default: (barcode) => `/prescriptions/basket/${barcode}`,
		v2: (barcode) => `/prescriptions/barcode/${barcode}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_ASSIGN_SECRETARY_PRESCRIPTION]: {
		default: (prescription) => `/prescriptions/${prescription.id}/assignSecretary`,
		v2: (prescription) => `/prescriptions/${prescription.id}/assignSecretary`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_UNASSIGN_SECRETARY_PRESCRIPTION]: {
		default: (prescription) => `/prescriptions/${prescription.id}/unassignSecretary`,
		v2: (prescription) => `/prescriptions/${prescription.id}/unassignSecretary`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_VALIDATE_PRESCRIPTION]: {
		default: (prescription) => `/prescriptions/${prescription.id}/validate`,
		v2: (prescription) => `/prescriptions/${prescription.id}/validate`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_POST_SAFEPLACE_RESULT_PRESCRIPTION]: {
		default: (prescription) => `/prescriptions/${prescription.id}/result`,
		v2: (prescription) => `/prescriptions/${prescription.id}/result`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_POST_IATA_DID_PRESCRIPTION]: {
		default: (prescription) => `/prescriptions/${prescription.id}/iata`,
		v2: (prescription) => `/prescriptions/${prescription.id}/iata`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_SET_SAMPLER_PRESCRIPTION]: {
		default: (prescription) => `/prescriptions/${prescription.id}/setSampler`,
		v2: (prescription) => `/prescriptions/${prescription.id}/setSampler`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_GET_REPEATABLE_PRESCRIPTION]: {
		default: "/prescriptions/repeatable",
		v2: "/prescriptions/repeatable",
		currentClient: DEFAULT_CLIENT,
	},
	[API_POST_REPEATABLE_PRESCRIPTION]: {
		default: (prescriptionId) => `/prescriptions/repeatable/${prescriptionId}`,
		v2: (prescriptionId) => `/prescriptions/repeatable/${prescriptionId}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_POST_PRESCRIPTION_SCAN_BARCODE]: {
		default: (prescriptionScanId) => `/prescriptions/scan/${prescriptionScanId}`,
		v2: (prescriptionScanId) => `/prescriptions/scan/${prescriptionScanId}/prescri`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_PATCH_PRESCRIPTION_CONTACT]: {
		default: (prescriptionId) => `/prescriptions/${prescriptionId}/contact`,
		v2: (prescriptionId) => `/prescriptions/${prescriptionId}/contact`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_GET_FPA_PDF]: {
		default: (prescriptionId) => `/prescriptions/fpa/${prescriptionId}`,
		v2: (prescriptionId) => `/prescriptions/fpa/${prescriptionId}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_GET_EPN_PDF]: {
		default: (prescriptionId) => `/prescriptions/aes/${prescriptionId}`,
		v2: (prescriptionId) => `/prescriptions/aes/${prescriptionId}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_PATCH_CHECK_ANALYSIS]: {
		default: (prescriptionId, analysisId) => `/prescription/${prescriptionId}/check/${analysisId}`,
		v2: (prescriptionId, analysisId) => `/prescriptions/prescription/${prescriptionId}/check/${analysisId}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_GET_SCANS]: {
		default: "/scan/ordo/center/:center",
		currentClient: DEFAULT_CLIENT,
	},
	[API_GET_ALL_SCANS]: {
		default: (prescriptionId) => `scan/${prescriptionId}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_PUT_SCAN_ORDO]: {
		default: (scanOrdoId, prescriptionId) => `/scan/ordo/use/${scanOrdoId}/${prescriptionId}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_DELETE_SCAN_ORDO]: {
		default: (scanOrdoId) => `/scan/ordo/${scanOrdoId}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_GET_MY_SECU_CHECK]: {
		default: "/dsp/mysecu",
		currentClient: DEFAULT_CLIENT,
	},
	[API_GET_RELOAD_SCANS]: {
		default: (prescriptionId, barcode) => `/scan/${prescriptionId}/barcode/${barcode}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_GET_MYLAB_ORDO]: {
		default: (prescriptionId) => `/prescriptions/oldDoc/${prescriptionId}`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_PUT_REFUSED_ANALYSIS]: {
		default: (analysisId) => `/analysis/${analysisId}/refused`,
		currentClient: DEFAULT_CLIENT,
	},
	[API_PATCH_DISPENSES_ANALYSES]: {
		default: "/dsp/prescription/dispense",
		currentClient: DEFAULT_CLIENT,
	},
	[API_GET_DSP_PRESCRIPTION]: {
		default: "/dsp/prescription",
		currentClient: DEFAULT_CLIENT,
	},
}

export function postPrescription(prescription, folderId) {
	const version = getVersion(API_POST_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescription) : version.path;
	const data = version.currentClient === V2_CLIENT 
		? adaptPrescriptionBody(prescription) 
		: prescription;
	
	return {
		type: API_POST_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "POST",
				data: data,
			},
			folderId: folderId,
		},
	};
}

export function patchPrescription(prescription, folderId) {
	const version = getVersion(API_PATCH_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescription) : version.path;
	const data = version.currentClient === V2_CLIENT 
		? adaptPrescriptionBody(prescription) 
		: prescription;
	
	return {
		type: API_PATCH_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "PATCH",
				data: data,
			},
			folderId: folderId,
		},
	};
}

export function deletePrescription(prescription, folderId) {
	const version = getVersion(API_DELETE_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescription) : version.path;
	return {
		type: API_DELETE_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "DELETE",
			},
			folderId: folderId,
			prescriptionId: prescription.id,
		},
	};
}

export function postScanPrescription(data, prescriptionId, folderId) {
	const version = getVersion(API_POST_SCAN_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescriptionId) : version.path;
	return {
		type: API_POST_SCAN_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				config: { headers: { "Content-Type": "multipart/form-data" } },
				url: url,
				method: "POST",
				data: data,
			},
			prescriptionId: prescriptionId,
			folderId: folderId,
		},
	};
}

export function deleteScanPrescription(prescriptionScan, prescriptionId, folderId) {
	const version = getVersion(API_DELETE_SCAN_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescriptionScan) : version.path;
	return {
		type: API_DELETE_SCAN_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "DELETE",
			},
			prescriptionId: prescriptionId,
			folderId: folderId,
		},
	};
}

export function updateScanPrescription(prescriptionScanId, prescriptionId, oldPrescriptionId, folderId) {
	const version = getVersion(API_POST_SCAN_PRESCRIPTION_UPDATE_PRESC);
	const url = typeof version.path === 'function' ? version.path(prescriptionScanId) : version.path;
	return {
		type: API_POST_SCAN_PRESCRIPTION_UPDATE_PRESC,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "POST",
				data: { prescriptionId: prescriptionId },
			},
			prescriptionId: prescriptionId,
			folderId: folderId,
			oldPrescriptionId: oldPrescriptionId,
		},
	};
}

export function validateScanPrescription(prescriptionScan, prescriptionId, folderId) {
	const version = getVersion(API_PUT_VALID_PRESCRIPTION_SCAN);
	const url = typeof version.path === 'function' ? version.path(prescriptionScan) : version.path;
	return {
		type: API_PUT_VALID_PRESCRIPTION_SCAN,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "PUT",
			},
			prescriptionId: prescriptionId,
			folderId: folderId,
		},
	};
}

export function validateQualityScanPrescription(prescriptionScan, prescriptionId, folderId) {
	const version = getVersion(API_PUT_QUALITY_PRESCRIPTION_SCAN);
	const url = typeof version.path === 'function' ? version.path(prescriptionScan) : version.path;
	return {
		type: API_PUT_QUALITY_PRESCRIPTION_SCAN,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "PUT",
			},
			prescriptionId: prescriptionId,
			folderId: folderId,
		},
	};
}
export function unvalidateQualityScanPrescription(prescriptionScan, prescriptionId, folderId) {
	const version = getVersion(API_PUT_BAD_QUALITY_PRESCRIPTION_SCAN);
	const url = typeof version.path === 'function' ? version.path(prescriptionScan) : version.path;
	return {
		type: API_PUT_BAD_QUALITY_PRESCRIPTION_SCAN,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "PUT",
			},
			prescriptionId: prescriptionId,
			folderId: folderId,
		},
	};
}

export function bulkAnalysesPrescription(data, prescription, folderId) {
	const version = getVersion(API_BULK_ANALYSES_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescription) : version.path;
	return {
		type: API_BULK_ANALYSES_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "PATCH",
				data: data,
			},
			folderId: folderId,
		},
	};
}

export function getPrescriptionByBarcode(barcode) {
	const version = getVersion(API_GET_BY_BARCODE_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(barcode) : version.path;
	return {
		type: API_GET_BY_BARCODE_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "GET",
			},
		},
	};
}

export function assignSecretary(prescription) {
	const version = getVersion(API_ASSIGN_SECRETARY_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescription) : version.path;
	return {
		type: API_ASSIGN_SECRETARY_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "POST",
				data: {},
			},
		},
	};
}

export function unassignSecretary(prescription) {
	const version = getVersion(API_UNASSIGN_SECRETARY_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescription) : version.path;
	return {
		type: API_UNASSIGN_SECRETARY_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "POST",
				data: {},
			},
		},
	};
}

export function validatePrescription(prescription) {
	const version = getVersion(API_VALIDATE_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescription) : version.path;
	return {
		type: API_VALIDATE_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "POST",
				data: {},
			},
		},
	};
}

export function saveResultPrescription(prescription, result) {
	const version = getVersion(API_POST_SAFEPLACE_RESULT_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescription) : version.path;
	return {
		type: API_POST_SAFEPLACE_RESULT_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "POST",
				data: result,
			},
		},
	};
}

export function saveIataDIDPrescription(prescription, iataDID) {
	const version = getVersion(API_POST_IATA_DID_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescription) : version.path;
	return {
		type: API_POST_IATA_DID_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "POST",
				data: { iataDID },
			},
		},
	};
}

export function setSampler(prescription) {
	const version = getVersion(API_SET_SAMPLER_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescription) : version.path;
	return {
		type: API_SET_SAMPLER_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "POST",
				data: {},
			},
		},
	};
}

export function getRepeatablePresciptions(patientId) {
	const version = getVersion(API_GET_REPEATABLE_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(patientId) : version.path;
	return {
		type: API_GET_REPEATABLE_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "get",
				params: {
					patientId,
				},
			},
		},
	};
}

export function repeatPresciption(prescriptionId, folderId) {
	const version = getVersion(API_POST_REPEATABLE_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(prescriptionId) : version.path;
	return {
		type: API_POST_REPEATABLE_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "POST",
				data: {
					folderId,
				},
			},
		},
	};
}

export function setPrescriptionScanBarcode(prescriptionScanId, barcode) {
	const version = getVersion(API_POST_PRESCRIPTION_SCAN_BARCODE);
	const url = typeof version.path === 'function' ? version.path(prescriptionScanId) : version.path;
	return {
		type: API_POST_PRESCRIPTION_SCAN_BARCODE,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "POST",
				data: {
					barcode,
				},
			},
		},
	};
}

export function toggleContact(prescriptionId, field) {
	const version = getVersion(API_PATCH_PRESCRIPTION_CONTACT);
	const url = typeof version.path === 'function' ? version.path(prescriptionId) : version.path;
	return {
		type: API_PATCH_PRESCRIPTION_CONTACT,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "PATCH",
				data: {
					field,
				},
			},
		},
	};
}

export function getEPrescription(qrcode) {
	const version = getVersion(API_GET_DSP_PRESCRIPTION);
	const url = typeof version.path === 'function' ? version.path(qrcode) : version.path;
	return {
		type: API_GET_DSP_PRESCRIPTION,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "GET",
				params: {
					qrcode,
				},
			},
		},
	};
}

export function getFPA(prescriptionId) {
	const version = getVersion(API_GET_FPA_PDF);
	const url = typeof version.path === 'function' ? version.path(prescriptionId) : version.path;
	return {
		type: API_GET_FPA_PDF,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "GET",
			},
		},
	};
}
export function getEPrescriptionAESPDF(prescriptionId) {
	const version = getVersion(API_GET_EPN_PDF);
	const url = typeof version.path === 'function' ? version.path(prescriptionId) : version.path;
	return {
		type: API_GET_EPN_PDF,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "GET",
			},
		},
	};
}

export function getMyLabDocuments(prescriptionId) {
	const version = getVersion(API_GET_MYLAB_ORDO);
	const url = typeof version.path === 'function' ? version.path(prescriptionId) : version.path;
	return {
		type: API_GET_MYLAB_ORDO,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
				method: "GET",
			},
		},
	};
}

export function refreshCenterScans(center, barcode) {
	const version = getVersion(API_GET_SCANS);
	const url = !!barcode ? `/scan/ordo/barcode/${barcode}/${center}` : `/scan/ordo/center/${center}`;
	return {
		type: API_GET_SCANS,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
			},
		},
	};
}
export function getScansByCenter(center) {
	const version = getVersion(API_GET_SCANS);
	const url = `/scan/ordo/center/${center}`;
	return {
		type: API_GET_SCANS,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
			},
		},
	};
}
export function reloadScans(prescriptionId, barcode) {
	const version = getVersion(API_GET_RELOAD_SCANS);
	const url = typeof version.path === 'function' ? version.path(prescriptionId, barcode) : version.path;
	return {
		type: API_GET_RELOAD_SCANS,
		payload: {
			client: version.currentClient,
			request: {
				url: url,
			},
		},
	};
}

export function cleanScans() {
	return {
		type: CLEAN_SCANS,
	};
}

export function useScanOrdo(scanOrdoId, prescriptionId) {
	const version = getVersion(API_PUT_SCAN_ORDO);
	const url = typeof version.path === 'function' ? version.path(scanOrdoId, prescriptionId) : version.path;
	return {
		type: API_PUT_SCAN_ORDO,
		payload: {
			client: version.currentClient,
			request: {
				method: "PUT",
				url: url,
			},
		},
	};
}

export function removeScanOrdo(scanOrdoId) {
	const version = getVersion(API_DELETE_SCAN_ORDO);
	const url = typeof version.path === 'function' ? version.path(scanOrdoId) : version.path;
	return {
		type: API_DELETE_SCAN_ORDO,
		payload: {
			client: version.currentClient,
			request: {
				method: "DELETE",
				url: url,
			},
		},
	};
}

export function checkMySecu(matricule, doctor_code, prescription_date, prescription_id, is_open, accident_date = null, accident_number = null) {
	const version = getVersion(API_GET_MY_SECU_CHECK);
	const url = typeof version.path === 'function' ? version.path() : version.path;
	return {
		type: API_GET_MY_SECU_CHECK,
		payload: {
			client: version.currentClient,
			request: {
				method: "GET",
				url: url,
				params: {
					matricule,
					doctor_code,
					prescription_date,
					prescription_id,
					is_open,
					accident_date: accident_date ? accident_date : null,
					accident_number: accident_number ? accident_number : null,
				},
			},
		},
	};
}

export function cleanMySecuCheck() {
	return {
		type: CLEAN_MY_SECU_CHECK,
	};
}

export function refuseAnalysis(analysisId, refused) {
	const version = getVersion(API_PUT_REFUSED_ANALYSIS);
	const url = typeof version.path === 'function' ? version.path(analysisId) : version.path;
	return {
		type: API_PUT_REFUSED_ANALYSIS,
		payload: {
			client: version.currentClient,
			request: {
				method: "PUT",
				url: url,
				params: {
					refused,
				},
			},
		},
	};
}

export function dispenseAnalyses(prescriptionUid, analyses, cancel = false) {
	const version = getVersion(API_PATCH_DISPENSES_ANALYSES);
	const url = typeof version.path === 'function' ? version.path() : version.path;
	return {
		type: API_PATCH_DISPENSES_ANALYSES,
		payload: {
			client: version.currentClient,
			request: {
				method: "PATCH",
				url: url,
				data: {
					qrcode: prescriptionUid,
					analyses,
					cancel,
				},
			},
		},
	};
}

export function checkAnalysis(folderId, prescriptionId, analysisId, checked) {
	const version = getVersion(API_PATCH_CHECK_ANALYSIS);
	const url = typeof version.path === 'function' ? version.path(prescriptionId, analysisId) : version.path;
	return {
		type: API_PATCH_CHECK_ANALYSIS,
		payload: {
			client: version.currentClient,
			request: {
				method: "PATCH",
				url: url,
				data: {
					checked,
				},
			},
			folderId,
		},
	};
}
export function getAllPrescriptionScans(prescriptionId) {
	const version = getVersion(API_GET_ALL_SCANS);
	const url = typeof version.path === 'function' ? version.path(prescriptionId) : version.path;
	return {
		type: API_GET_ALL_SCANS,
		payload: {
			client: version.currentClient,
			request: {
				method: "GET",
				url: url,
			},
		},
	};
}
